import { noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorPageInternal, DashboardPieChart, shadCnColors } from 'noddi-ui-web';
import type { FleetViewFilterInput } from '../fleetViewSummaryFilterSpecs';
import { UpcomingBookingsChartTranslations } from '../fleetViewTranslations';
import { useFleetViewSummaryFilters } from '../useFleetViewSummaryFilters';

const CHART_VALUES = {
  HAS_UPCOMING: 'true',
  NO_UPCOMING: 'false'
} as const;

type Props = {
  filterParams: FleetViewFilterInput;
  translations: UpcomingBookingsChartTranslations;
};

export const UpcomingBookingsChart = ({ filterParams, translations: t }: Props) => {
  const { hasBookingItemUpcoming, setHasBookingItemUpcoming } = useFleetViewSummaryFilters();

  const {
    data: summary,
    isPending,
    error: summaryError
  } = noddiAsync.useGet({
    type: URLKeys.getFleetViewSummary,
    input: filterParams
  });

  const carsWithUpcomingBooking = summary?.carsWithUpcomingBooking ?? 0;
  const totalCars = summary?.totalCars ?? 0;

  const handleSelectionChange = (values: string[]) => {
    if (values.length === 0) {
      setHasBookingItemUpcoming(null);
      return;
    }
    // Get the most recently clicked value (last in array)
    const lastValue = values[values.length - 1];
    setHasBookingItemUpcoming(lastValue === CHART_VALUES.HAS_UPCOMING);
  };

  const getSelectedValues = (): string[] => {
    if (hasBookingItemUpcoming === null || hasBookingItemUpcoming === undefined) {
      return [];
    }
    return hasBookingItemUpcoming ? [CHART_VALUES.HAS_UPCOMING] : [CHART_VALUES.NO_UPCOMING];
  };

  const data = [
    {
      name: t.hasUpcomingBooking,
      value: CHART_VALUES.HAS_UPCOMING,
      count: carsWithUpcomingBooking,
      color: shadCnColors.green[500]
    },
    {
      name: t.noUpcomingBooking,
      value: CHART_VALUES.NO_UPCOMING,
      count: totalCars - carsWithUpcomingBooking,
      color: shadCnColors.gray[300]
    }
  ];

  const description = t.carsWithUpcomingBookings(carsWithUpcomingBooking, totalCars);

  if (summaryError) {
    return <ApiErrorPageInternal error={summaryError} />;
  }

  return (
    <DashboardPieChart
      title={t.upcomingBookings}
      description={description}
      data={data}
      emptyStateTitle={t.noCarsInFleet}
      size='xs'
      selectedValues={getSelectedValues()}
      onSelectionChange={handleSelectionChange}
      isLoading={isPending}
    />
  );
};
